import React, { useContext, useEffect, useState } from "react";
import { Table, Form, Button, Modal } from "react-bootstrap";

import Utils from "../../../../../Utils";
import ContextComponent from "../../../../../AppContext";

import DeleteIcon from "../../../../../../assets/img/Delete.png";

const createEmptyRange = () => ({
  minScore: "",
  maxScore: "",
  resultTitle: "",
  resultDescription: "",
  colorCode: "#232762",
});

const EditScoreRange = ({ props }) => {
  const { setAlert, setLoadingState, setCanRedirectToLogin } =
    useContext(ContextComponent);

  const [ranges, setRanges] = useState([]);

  useEffect(() => {
    if (!props?.editModalCanOpen) {
      return;
    }

    const existingRanges = props?.existingRanges || [];

    if (existingRanges.length === 0) {
      setRanges([createEmptyRange()]);
      return;
    }

    const formattedRanges = existingRanges.map((range) => ({
      id: range?.id,

      minScore:
        range?.minScore !== undefined && range?.minScore !== null
          ? String(range.minScore)
          : "",

      maxScore:
        range?.maxScore !== undefined && range?.maxScore !== null
          ? String(range.maxScore)
          : "",

      colorCode: range?.colorCode || "#232762",

      resultTitle: range?.resultTitle || "",

      resultDescription: range?.resultDescription || "",
    }));

    setRanges(formattedRanges);
  }, [props?.editModalCanOpen, props?.existingRanges]);

  const hideModal = () => {
    props.setEditModalCanOpen(false);
  };

  const handleFieldChange = (index, field, value) => {
    setRanges((previousRanges) => {
      const updatedRanges = [...previousRanges];

      updatedRanges[index] = {
        ...updatedRanges[index],
        [field]: value,
      };

      return updatedRanges;
    });
  };

  const addRow = () => {
    setRanges((previousRanges) => [...previousRanges, createEmptyRange()]);
  };

  const removeRow = (index) => {
    if (ranges.length === 1) {
      setAlert({
        show: true,
        message: "At least one score range is required.",
        type: "danger",
      });

      return;
    }

    setRanges((previousRanges) =>
      previousRanges.filter((_, rowIndex) => rowIndex !== index),
    );
  };

  //    Validate all rows before updating.
  const validateRanges = () => {
    if (!ranges || ranges.length === 0) {
      setAlert({
        show: true,
        message: "At least one score range is required.",
        type: "danger",
      });

      return false;
    }

    for (let index = 0; index < ranges.length; index++) {
      const currentRange = ranges[index];

      //  Minimum score.

      if (
        currentRange.minScore === "" ||
        Number.isNaN(Number(currentRange.minScore))
      ) {
        setAlert({
          show: true,
          message: `Minimum score is required for row ${index + 1}.`,
          type: "danger",
        });

        return false;
      }

      //    Maximum score.
      if (
        currentRange.maxScore === "" ||
        Number.isNaN(Number(currentRange.maxScore))
      ) {
        setAlert({
          show: true,
          message: `Maximum score is required for row ${index + 1}.`,
          type: "danger",
        });

        return false;
      }

      const minScore = Number(currentRange.minScore);

      const maxScore = Number(currentRange.maxScore);

      //    Scores cannot be negative.
      if (minScore < 0 || maxScore < 0) {
        setAlert({
          show: true,
          message: `Scores cannot be negative in row ${index + 1}.`,
          type: "danger",
        });

        return false;
      }

      //    Maximum must be greater than minimum.
      if (maxScore <= minScore) {
        setAlert({
          show: true,
          message:
            `Maximum score must be greater than minimum ` +
            `score in row ${index + 1}.`,
          type: "danger",
        });

        return false;
      }

      if (Utils.isEmpty(currentRange.colorCode)) {
        setAlert({
          show: true,
          message: `Color code is required for row ${index + 1}.`,
          type: "danger",
        });

        return false;
      }

      if (index > 0) {
        const previousRange = ranges[index - 1];

        const previousMaxScore = Number(previousRange.maxScore);

        if (previousRange.maxScore === "" || Number.isNaN(previousMaxScore)) {
          setAlert({
            show: true,
            message: `Please enter a valid maximum score in ` + `row ${index}.`,
            type: "danger",
          });

          return false;
        }

        if (minScore <= previousMaxScore) {
          setAlert({
            show: true,
            message:
              `Minimum score in row ${index + 1} ` +
              `must be greater than the maximum score ` +
              `of row ${index}. Previous maximum: ` +
              `${previousMaxScore}`,
            type: "danger",
          });

          return false;
        }
      }

      if ((currentRange.resultTitle || "").length > 100) {
        setAlert({
          show: true,
          message:
            `Result title cannot exceed 100 characters ` +
            `in row ${index + 1}.`,
          type: "danger",
        });

        return false;
      }

      if ((currentRange.resultDescription || "").length > 500) {
        setAlert({
          show: true,
          message:
            `Result description cannot exceed 500 ` +
            `characters in row ${index + 1}.`,
          type: "danger",
        });

        return false;
      }
    }

    return true;
  };

  const updateScoreRanges = () => {
    if (!validateRanges()) {
      return;
    }

    if (!props?.scoreRangesGroup?.id) {
      setAlert({
        show: true,
        message: "Score range group was not found.",
        type: "danger",
      });

      return;
    }

    const payload = {
      name: props?.scoreRangesGroup?.name || "Score Range",

      siteId: props?.siteId,

      ranges: ranges.map((range, index) => ({
        minScore: Number(range.minScore),
        maxScore: Number(range.maxScore),
        colorCode: range.colorCode,
        resultTitle: range.resultTitle || "",
        resultDescription: range.resultDescription || "",
        displayOrder: index + 1,
      })),
    };

    setLoadingState({
      applyMask: true,
      text: "Updating score ranges",
    });

    const updateSuccess = (res) => {
      setLoadingState({
        applyMask: false,
      });

      setAlert({
        show: true,
        message: res?.data?.message || "Score ranges updated successfully!",
        type: "success",
      });

      hideModal();

      props.loadFreshScoreRanges();
    };

    const updateFailure = (err) => {
      setLoadingState({
        applyMask: false,
      });

      if (err?.redirect) {
        setCanRedirectToLogin(true);
      }

      setAlert({
        show: true,
        message: err?.data?.message || "Score ranges update failed",
        type: "danger",
      });

      console.log(err);
    };

    Utils.updateScoreRanges(props?.scoreRangesGroup?.id, payload).then(
      updateSuccess,
      updateFailure,
    );
  };

  return (
    <Modal
      centered
      show={props.editModalCanOpen}
      className="indi-full-modal"
      onHide={hideModal}
    >
      <Modal.Header closeButton>
        <Modal.Title>Edit Score Range</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <form className="indi-add-form-wrapper">
          <div className="indi-add-key-value-item d-flex flex-column">
            <Table responsive="sm">
              <thead className="indi-data-table-header">
                <tr>
                  <th>Minimum Score</th>
                  <th>Maximum Score</th>
                  <th>Result Title</th>
                  <th>Result Description</th>
                  <th>Color Code</th>
                  <th>Delete</th>
                </tr>
              </thead>

              <tbody>
                {ranges.map((range, index) => (
                  <tr
                    key={range?.id || `edit-range-${index}`}
                    className="indi-data-table1-tr"
                  >
                    {/* Minimum Score */}
                    <td>
                      <div className="indi-data-table1-wrapper d-flex indi-text-area-row">
                        <Form.Control
                          type="number"
                          step="0.1"
                          min="0"
                          className="indi-input-field"
                          placeholder="Enter Minimum Score"
                          autoComplete="off"
                          value={range.minScore}
                          onChange={(e) =>
                            handleFieldChange(index, "minScore", e.target.value)
                          }
                        />
                      </div>
                    </td>

                    {/* Maximum Score */}
                    <td>
                      <div className="indi-data-table1-wrapper d-flex indi-text-area-row">
                        <Form.Control
                          type="number"
                          step="0.1"
                          min="0"
                          className="indi-input-field"
                          placeholder="Enter Maximum Score"
                          autoComplete="off"
                          value={range.maxScore}
                          onChange={(e) =>
                            handleFieldChange(index, "maxScore", e.target.value)
                          }
                        />
                      </div>
                    </td>

                    {/* Result Title */}
                    <td>
                      <div className="indi-data-table1-wrapper d-flex indi-text-area-row">
                        <Form.Control
                          type="text"
                          className="indi-input-field"
                          placeholder="Enter Result Title"
                          maxLength={100}
                          autoComplete="off"
                          value={range.resultTitle}
                          onChange={(e) =>
                            handleFieldChange(
                              index,
                              "resultTitle",
                              e.target.value,
                            )
                          }
                        />
                      </div>
                    </td>

                    {/* Result Description */}
                    <td>
                      <div className="indi-data-table1-wrapper d-flex indi-text-area-row">
                        <Form.Control
                          as="textarea"
                          className="indi-input-field"
                          placeholder="Enter Result Description"
                          maxLength={500}
                          autoComplete="off"
                          value={range.resultDescription}
                          onChange={(e) =>
                            handleFieldChange(
                              index,
                              "resultDescription",
                              e.target.value,
                            )
                          }
                        />
                      </div>
                    </td>

                    {/* Color Code */}
                    <td>
                      <div className="indi-add-form-item d-flex">
                        <div className="col">
                          <Form.Control
                            type="color"
                            className="indi-input-field indi-color-input-field"
                            value={range.colorCode || "#232762"}
                            title="Select color"
                            onChange={(e) =>
                              handleFieldChange(
                                index,
                                "colorCode",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                      </div>
                    </td>

                    {/* Delete */}
                    <td>
                      <div className="d-flex">
                        <img
                          className="indi-add-question-delete-icon-img"
                          src={DeleteIcon}
                          alt="delete"
                          onClick={() => removeRow(index)}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {/* Add Row */}
            <div className="w-100">
              <Button
                onClick={addRow}
                className="float-end"
                style={{
                  fontSize: "0.7rem",
                  padding: "0.2rem 0.5rem",
                }}
              >
                Add Row
              </Button>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="indi-add-footer">
          <div className="indi-add-page-footer-btn-wrapper float-right">
            <button
              type="button"
              className="btn btn-primary"
              onClick={hideModal}
            >
              Cancel
            </button>

            <button
              type="button"
              className="btn btn-primary"
              onClick={updateScoreRanges}
            >
              Update
            </button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default EditScoreRange;
